import { useEffect, useState } from "react";
import { Download, Loader2, RotateCcw } from "lucide-react";
import { getVerificationVideo } from "@/lib/apiClient";

export default function AdminVerificationVideo({ applicationId, referencePhoto }) {
  const [attempt, setAttempt] = useState(0);
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    let objectUrl;
    setUrl("");
    setError("");
    getVerificationVideo(applicationId).then((blob) => {
      if (cancelled) return;
      objectUrl = URL.createObjectURL(blob);
      setUrl(objectUrl);
    }).catch(async (err) => {
      let message = "Could not load this recording. Please retry.";
      try {
        const details = JSON.parse(await err.response.data.text());
        if (typeof details.detail === "string") message = details.detail;
      } catch { /* Network errors have no response body. */ }
      if (!cancelled) setError(message);
    });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [applicationId, attempt]);

  return (
    <div data-testid="admin-video-review" className="space-y-3">
      <p data-testid="admin-video-review-notice" className="text-xs text-black/60">
        Private recording · Manual review. Check the spoken code and compare the face with the submitted photos; no automated match has been performed.
      </p>
      {!url && !error && <p data-testid="admin-video-loading" role="status" className="flex items-center gap-2 text-sm py-8"><Loader2 size={16} className="animate-spin" /> Loading private recording…</p>}
      {error && <div data-testid="admin-video-error" role="alert" className="space-y-3 border border-red-200 bg-red-50 p-4 text-sm text-red-900">
        <p>{error}</p>
        <button type="button" data-testid="admin-video-retry" onClick={() => setAttempt((n) => n + 1)} className="inline-flex items-center gap-2 underline"><RotateCcw size={14} /> Retry video</button>
      </div>}
      {url && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
            <div>
              <p className="text-xs text-black/50 mb-2">Recorded clip</p>
              <video key={url} src={url} controls playsInline preload="auto" onError={() => setError("This browser could not play the recording. Download the MP4, or retry loading it.")} className="w-full aspect-[4/3] object-contain bg-black rounded-sm" data-testid="admin-verification-video" />
            </div>
            {referencePhoto && <div>
              <p className="text-xs text-black/50 mb-2">Submitted photo</p>
              <img data-testid="admin-verification-reference-photo" src={referencePhoto} alt="Submitted photo for manual comparison" className="w-full aspect-[4/3] object-contain bg-neutral-100 rounded-sm" />
            </div>}
          </div>
          <a data-testid="admin-video-download" href={url} download={`verification-${applicationId}.mp4`} className="inline-flex items-center gap-2 text-xs underline underline-offset-4"><Download size={14} /> Download MP4</a>
        </>
      )}
    </div>
  );
}
